import React, { Dispatch, ReactNode, createContext, useContext, useMemo, useReducer } from 'react';

import { ITab } from '../../../CommonComponents/TabBar/TabBar';

// UI-only state shared across the Setup Online Publication tree. Async server
// state (brandings, toc settings, menus, etc.) lives in TanStack Query; only
// UI ephemera belongs here.
export type SetupOnlinePublicationUiState = {
  activeTab: ITab | null;
  selectedMenuId: string | null;
  isOrganizationMenuOrStoriesInProgress: boolean;
  // Toggled `true` by mutation handlers that want the live-preview link to
  // refresh; flipped back to `false` by the Header after it regenerates the
  // preview URL.
  updatePublicationPreview: boolean;
};

export type SetupOnlinePublicationUiAction =
  | { type: 'SET_ACTIVE_TAB'; tab: ITab | null }
  | { type: 'SET_SELECTED_MENU_ID'; menuId: string | null }
  | { type: 'SET_ORGANIZATION_IN_PROGRESS'; inProgress: boolean }
  | { type: 'SET_UPDATE_PUBLICATION_PREVIEW'; needsRefresh: boolean };

export const initialSetupOnlinePublicationUiState: SetupOnlinePublicationUiState = {
  activeTab: null,
  selectedMenuId: null,
  isOrganizationMenuOrStoriesInProgress: false,
  updatePublicationPreview: false
};

export function setupOnlinePublicationUiReducer(
  state: SetupOnlinePublicationUiState,
  action: SetupOnlinePublicationUiAction
): SetupOnlinePublicationUiState {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.tab };
    case 'SET_SELECTED_MENU_ID':
      return { ...state, selectedMenuId: action.menuId };
    case 'SET_ORGANIZATION_IN_PROGRESS':
      return { ...state, isOrganizationMenuOrStoriesInProgress: action.inProgress };
    case 'SET_UPDATE_PUBLICATION_PREVIEW':
      return { ...state, updatePublicationPreview: action.needsRefresh };
    default:
      return state;
  }
}

type ContextValue = {
  state: SetupOnlinePublicationUiState;
  dispatch: Dispatch<SetupOnlinePublicationUiAction>;
};

// Safe defaults so any component renders under tests without a Provider
// wrapping it. Dispatch is a no-op until the real Provider mounts.
const noopDispatch: Dispatch<SetupOnlinePublicationUiAction> = () => undefined;

const SetupOnlinePublicationUiContext = createContext<ContextValue>({
  state: initialSetupOnlinePublicationUiState,
  dispatch: noopDispatch
});

type ProviderProps = {
  children: ReactNode;
  initialState?: Partial<SetupOnlinePublicationUiState>;
};

export function SetupOnlinePublicationUiProvider({ children, initialState }: ProviderProps) {
  const [state, dispatch] = useReducer(setupOnlinePublicationUiReducer, {
    ...initialSetupOnlinePublicationUiState,
    ...initialState
  });

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <SetupOnlinePublicationUiContext.Provider value={value}>
      {children}
    </SetupOnlinePublicationUiContext.Provider>
  );
}

export function useSetupOnlinePublicationUi(): ContextValue {
  return useContext(SetupOnlinePublicationUiContext);
}
